import hydrateThreadMeta from '@/redux/actions/pieveActions/hydrateThreadMeta.js';
import { decodeJwtPayload, randomId } from '@shared/utils';
import getPieveAuthToken from '@/lib/auth/getPieveAuthToken.js';
import getPieveApiBaseUrl from '@/lib/getPieveApiBaseUrl.js';
import normalizePieveThreadEventForPresentation, { normalizePieveThreadEventsForPresentation } from '@/lib/pieveThreadEventNormalization.js';
import getPieveCompanyIdForRequest from '@/lib/auth/getPieveCompanyIdForRequest.js';
import getPieveRuntimeInstanceHeader from '@/lib/apps/getPieveRuntimeInstanceHeader.js';
import getModelReasoningLevels from '@/lib/model/getModelReasoningLevels.js';
import createSseParser from '@/lib/platform/runtimeTransport/createSseParser.js';
import { getWebRuntimeContext, resolveRuntimeProjectContext } from '@/lib/platform/runtimeTransport/runtimeContextHelpers.js';
import startManagedRemoteRunStream from '@/lib/platform/runtimeTransport/startManagedRemoteRunStream.js';
import { normalizeThreadRunState, normalizeThreadRunStatePayload } from '@/lib/platform/runtimeTransport/threadPayloadHelpers.js';
import getThreadContentItemByThreadId from '@/lib/threads/getThreadContentItemByThreadId.js';
import deleteThreadEvent from '@/redux/actions/pieveActions/deleteThreadEvent.js';
import handleThreadRuntimeEvent from '@/redux/actions/pieveActions/handleThreadRuntimeEvent.js';
import setThreadRunState from '@/redux/actions/pieveActions/setThreadRunState.js';
import setThreadUpdatedAt from '@/redux/actions/pieveActions/setThreadUpdatedAt.js';
import upsertThreadEvent from '@/redux/actions/pieveActions/upsertThreadEvent.js';
import appendOptimisticUserBubble, { buildOptimisticUserEvent, toLines } from '@/lib/threadEvents/appendOptimisticUserBubble.js';
import { invalidateApiRequestGetDedupeState } from '@/redux/requests/apiRequest.js';
import listThreadEventsPage from '@/redux/requests/threads/listThreadEventsPage.js';
import { cancelThreadRun, startThreadRun, steerThreadRun } from '@/redux/requests/threadRunRequests.js';

const PIEVE_STREAM_PROTOCOL_VERSION = 'pieve.thread.run.v2';
const DEFAULT_STREAM_BACKFILL_LIMIT = 120;
const EXPLICIT_REMOTE_RUN_STREAM_OWNER = 'redux-explicit';
const HYDRATED_REMOTE_RUN_STREAM_OWNER = 'redux-hydrated';
const RUN_STREAM_EVENT_TYPES_THAT_INVALIDATE_GET_DEDUPE = new Set(['event.appended', 'event.upsert', 'event.deleted', 'threadeventappended', 'threadeventupsert', 'threadeventdelete', 'run.state', 'threadrunstate', 'run.done', 'threads.changed', 'thread.updated', 'thread.archived', 'thread.restored']);
const DIRECT_REMOTE_RUN_MANAGED_EVENT_TYPES = new Set(['assistant.delta', 'assistantdelta', 'event.appended', 'event.upsert', 'event.deleted', 'threadeventappended', 'threadeventupsert', 'threadeventdelete', 'run.state', 'threadrunstate', 'run.done', 'run.web_search', 'threadwebsearch', 'context.usage', 'threadcontextwindowusage', 'threaddiffcard', 'workflow.updated', 'workflow.test.updated']);
const activeRunStateByRunId = new Map();

const createTimestamp = () => {
  return new Date().toISOString();
};

const sleep = async (durationMs, signal) => {
  const normalizedDurationMs = Number(durationMs);
  const waitMs = Number.isFinite(normalizedDurationMs) && normalizedDurationMs > 0 ? Math.floor(normalizedDurationMs) : 0;

  if (waitMs < 1) {
    return;
  }

  await new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, waitMs);

    const cleanup = () => {
      clearTimeout(timeoutId);

      if (signal) {
        signal.removeEventListener('abort', handleAbort);
      }
    };

    const handleAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (signal) {
      signal.addEventListener('abort', handleAbort, { once: true });
    }
  });
};

const normalizeError = (value) => {
  if (typeof value === 'string') {
    return value;
  }

  return value?.message || 'Unknown error';
};

const shouldAttachDirectRemoteRunStream = (state) => {
  const normalizedState = normalizeThreadRunState(state);

  if (!normalizedState) {
    return false;
  }

  return normalizedState !== 'idle' && normalizedState !== 'complete' && normalizedState !== 'error' && normalizedState !== 'canceled';
};

const getNormalizedRunId = (value) => {
  return String(value || '').trim();
};

const getNormalizedThreadId = (value) => {
  return String(value || '').trim();
};

const isDirectRemoteRunManagedEventType = (value) => {
  const normalizedEventType = String(value || '')
    .trim()
    .toLowerCase();

  if (!normalizedEventType) {
    return false;
  }

  return DIRECT_REMOTE_RUN_MANAGED_EVENT_TYPES.has(normalizedEventType);
};

const getRawEventsFromEventsPage = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  const data = payload && typeof payload === 'object' ? payload : {};

  if (Array.isArray(data?.events)) {
    return data.events;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.list)) {
    return data.list;
  }

  return [];
};

const getLastAppliedSeqFromState = ({ getState, threadId } = {}) => {
  const state = typeof getState === 'function' ? getState() || {} : {};
  const activity = getThreadContentItemByThreadId(state?.pieve?.threadContent, threadId)?.activity;

  if (!Array.isArray(activity) || activity.length < 1) {
    return null;
  }

  let maxSeq = null;

  for (const event of activity) {
    const nextSeq = Number(event?.seq);

    // Synthetic client-only events (e.g. the cancel notice bubble) use a fractional seq purely
    // to sort into the activity list; the server's seq is an integer cursor, so a fractional
    // value must never be sent back as afterSeq/lastAppliedSeq.
    if (!Number.isInteger(nextSeq)) {
      continue;
    }

    maxSeq = maxSeq === null ? nextSeq : Math.max(maxSeq, nextSeq);
  }

  return maxSeq;
};

const getThreadMetaItemByThreadId = ({ state, threadId } = {}) => {
  const normalizedThreadId = getNormalizedThreadId(threadId);

  if (!normalizedThreadId) {
    return null;
  }

  const threadItems = state?.pieve?.threads;
  const directMatch = threadItems?.[normalizedThreadId];

  if (directMatch && typeof directMatch === 'object') {
    return directMatch;
  }

  if (!Array.isArray(threadItems)) {
    return null;
  }

  return threadItems.find((thread) => getNormalizedThreadId(thread?.id || thread?.threadId) === normalizedThreadId) || null;
};

const getCanonicalThreadRunStateFromState = ({ getState, threadId } = {}) => {
  const state = typeof getState === 'function' ? getState() || {} : {};
  const threadContent = getThreadContentItemByThreadId(state?.pieve?.threadContent, threadId);
  const threadMeta = getThreadMetaItemByThreadId({
    state,
    threadId
  });

  return threadContent?.runState || threadMeta?.runState || null;
};

const buildRunStreamHeaders = ({ authToken } = {}) => {
  const companyId = getPieveCompanyIdForRequest();

  return {
    Accept: 'text/event-stream',
    ...(authToken ? { Authorization: `Bearer ${String(authToken)}` } : {}),
    'x-mindcloud-instance': getPieveRuntimeInstanceHeader(),
    'x-pieve-request-nonce': randomId(),
    'x-pieve-request-timestamp': String(Math.floor(Date.now() / 1000)),
    ...(companyId ? { 'x-company-id': companyId } : {})
  };
};

const parseRunStartResponse = (payload = {}) => {
  const data = payload && typeof payload === 'object' ? payload : {};
  const events = normalizePieveThreadEventsForPresentation(getRawEventsFromEventsPage(data?.events || data?.threadEvents || data));
  const run = data?.run && typeof data.run === 'object' ? data.run : {};
  const billing = data?.billing && typeof data.billing === 'object' && !Array.isArray(data.billing) ? data.billing : null;
  const requestedModelId = String(data?.requestedModelId || '').trim() || null;
  const effectiveModelId = String(data?.effectiveModelId || requestedModelId || '').trim() || null;
  const runId = data?.runId || run?.runId || run?.id || null;
  const userEventId = data?.userEventId || null;
  const assistantEventId = data?.assistantEventId || null;
  const getEventId = (event) => {
    const eventId = event?.eventId || event?.id || null;

    if (!eventId) {
      return null;
    }

    return String(eventId);
  };
  const findEventById = (eventId) => {
    if (!eventId) {
      return null;
    }

    const normalizedEventId = String(eventId);
    return events.find((event) => getEventId(event) === normalizedEventId) || null;
  };
  const isUserEvent = (event) => {
    const role = String(event?.payload?.role || event?.role || '').toLowerCase();
    const className = String(event?.className || '').toLowerCase();
    return role === 'user' || className === 'user';
  };
  const isAssistantEvent = (event) => {
    const role = String(event?.payload?.role || event?.role || '').toLowerCase();
    const className = String(event?.className || '').toLowerCase();
    return role === 'assistant' || className === 'assistant';
  };
  const matchesAssistantFallback = (event, excludedEventId) => {
    const eventId = getEventId(event);

    if (eventId && excludedEventId && eventId === String(excludedEventId)) {
      return false;
    }

    if (isAssistantEvent(event)) {
      return true;
    }

    const normalizedType = String(event?.type || event?.payload?.type || '').toLowerCase();

    if (normalizedType !== 'message' && normalizedType !== 'bubble') {
      return false;
    }

    return !isUserEvent(event);
  };
  const withInheritedRunId = (event) => {
    if (!event || typeof event !== 'object' || Array.isArray(event)) {
      return event;
    }

    const normalizedRunId = getNormalizedRunId(runId);

    if (!normalizedRunId || getNormalizedRunId(event?.runId)) {
      return event;
    }

    return {
      ...event,
      runId: normalizedRunId
    };
  };
  const userEvent = withInheritedRunId(data?.userEvent || findEventById(userEventId) || events.find((event) => isUserEvent(event)));
  const assistantEvent = withInheritedRunId(data?.assistantEvent || findEventById(assistantEventId) || events.find((event) => matchesAssistantFallback(event, userEventId || getEventId(userEvent))));

  return {
    runId,
    threadUpdatedAt: data.threadUpdatedAt || null,
    runState: data?.state || run?.state || 'generating',
    userEvent,
    assistantEvent,
    userEventId: userEventId || getEventId(userEvent) || null,
    assistantEventId: assistantEventId || getEventId(assistantEvent) || null,
    requestedModelId,
    effectiveModelId,
    billing
  };
};

const syncConfirmedThreadUpdatedAt = ({ dispatch, threadId, threadUpdatedAt, source }) => {
  if (typeof threadUpdatedAt !== 'string' || !Number.isFinite(Date.parse(threadUpdatedAt))) {
    console.error('[pieve-thread-run] successful user message response has missing or invalid threadUpdatedAt', {
      source,
      threadId,
      threadUpdatedAt
    });
    return;
  }

  dispatch(
    setThreadUpdatedAt({
      threadId,
      updatedAt: threadUpdatedAt
    })
  );
};

const dispatchStreamPayload = ({ dispatch, threadId, runId, assistantEventId, payload, streamEventName } = {}) => {
  const maybeType = String(streamEventName || payload?.type || '').trim();

  if (!maybeType) {
    return {
      runDone: false
    };
  }

  if (maybeType === 'stream.error') {
    return {
      runDone: false,
      streamFailure: {
        success: false,
        retryable: false,
        code: payload?.code,
        message: payload?.message || 'Unable to verify the final assistant response'
      }
    };
  }

  if (RUN_STREAM_EVENT_TYPES_THAT_INVALIDATE_GET_DEDUPE.has(maybeType.toLowerCase())) {
    invalidateApiRequestGetDedupeState();
  }

  const normalizedThreadId = getNormalizedThreadId(payload?.threadId || payload?.data?.threadId || threadId);
  const normalizedRunId = getNormalizedRunId(payload?.runId || payload?.data?.runId || payload?.run?.runId || payload?.run?.id || runId) || null;

  if (maybeType === 'assistant.delta') {
    const delta = String(payload?.delta || payload?.text || payload?.chunk || payload?.content || '');
    const eventId = payload?.eventId || payload?.assistantEventId || assistantEventId;

    if (!delta || !eventId || !normalizedThreadId) {
      return {
        runDone: false
      };
    }

    dispatch(
      handleThreadRuntimeEvent({
        type: maybeType,
        threadId: normalizedThreadId,
        runId: normalizedRunId,
        eventId,
        assistantEventId: eventId,
        textOffset: payload?.textOffset,
        delta
      })
    );

    return {
      runDone: false
    };
  }

  if (maybeType === 'run.done') {
    const doneEvent = payload?.event || payload?.data?.event || null;

    if (doneEvent && normalizedThreadId) {
      dispatch(
        handleThreadRuntimeEvent({
          type: 'event.upsert',
          threadId: normalizedThreadId,
          runId: normalizedRunId,
          event: {
            ...doneEvent,
            ...(getNormalizedRunId(doneEvent?.runId) ? {} : normalizedRunId ? { runId: normalizedRunId } : {})
          }
        })
      );
    }
  }

  dispatch(
    handleThreadRuntimeEvent({
      ...payload,
      type: maybeType,
      threadId: normalizedThreadId,
      runId: normalizedRunId
    })
  );

  return {
    runDone: maybeType === 'run.done'
  };
};

const backfillThreadEventsFromSeq = async ({ dispatch, getState, threadId, authToken, limit = DEFAULT_STREAM_BACKFILL_LIMIT } = {}) => {
  const normalizedThreadId = getNormalizedThreadId(threadId);
  const normalizedAuthToken = String(authToken || '').trim();

  if (!normalizedThreadId) {
    return {
      success: false,
      data: {
        message: 'threadId is required'
      }
    };
  }

  let afterSeq = getLastAppliedSeqFromState({
    getState,
    threadId: normalizedThreadId
  });
  let totalCount = 0;

  for (let page = 0; page < 8; page += 1) {
    const pageRes = await listThreadEventsPage({
      threadId: normalizedThreadId,
      authToken: normalizedAuthToken,
      afterSeq: afterSeq ?? undefined,
      limit
    });

    if (!pageRes?.success) {
      return pageRes;
    }

    const nextEvents = normalizePieveThreadEventsForPresentation(getRawEventsFromEventsPage(pageRes?.data));

    if (nextEvents.length < 1) {
      break;
    }

    for (const event of nextEvents) {
      dispatch(
        upsertThreadEvent({
          threadId: normalizedThreadId,
          event
        })
      );
    }

    totalCount += nextEvents.length;
    const newestSeq = Number(nextEvents.at(-1)?.seq);

    if (!Number.isFinite(newestSeq) || nextEvents.length < limit) {
      break;
    }

    afterSeq = newestSeq;
  }

  return {
    success: true,
    data: {
      count: totalCount
    }
  };
};

const openDirectRunStreamOnce = async ({ dispatch, getState, threadId, runId, assistantEventId, authToken, signal, includeLastAppliedSeq = true } = {}) => {
  const normalizedThreadId = getNormalizedThreadId(threadId);
  const normalizedRunId = getNormalizedRunId(runId);
  const normalizedAuthToken = String(authToken || '').trim();

  if (!normalizedThreadId || !normalizedRunId) {
    return {
      success: false,
      retryable: false,
      message: 'Missing run stream fields'
    };
  }

  const baseUrl = getPieveApiBaseUrl();
  const url = new URL(`${baseUrl}/v1/pieve/threads/${encodeURIComponent(normalizedThreadId)}/runs/${encodeURIComponent(normalizedRunId)}/stream`, baseUrl || window.location.origin);
  url.searchParams.set('protocolVersion', PIEVE_STREAM_PROTOCOL_VERSION);
  const lastAppliedSeq = getLastAppliedSeqFromState({
    getState,
    threadId: normalizedThreadId
  });

  if (includeLastAppliedSeq && Number.isFinite(lastAppliedSeq)) {
    url.searchParams.set('lastAppliedSeq', String(lastAppliedSeq));
  }

  let response;

  try {
    response = await fetch(url.toString(), {
      method: 'GET',
      headers: buildRunStreamHeaders({
        authToken: normalizedAuthToken
      }),
      // The web session is an http-only cookie; the stream needs it like every other request.
      credentials: 'include',
      signal
    });
  } catch (err) {
    return {
      success: false,
      canceled: signal?.aborted === true,
      retryable: signal?.aborted !== true,
      message: normalizeError(err)
    };
  }

  if (!response.ok) {
    return {
      success: false,
      status: response.status,
      retryable: response.status >= 500 || response.status === 429,
      message: 'Failed to open run stream'
    };
  }

  if (!response.body) {
    return {
      success: false,
      retryable: true,
      message: 'SSE stream body is unavailable'
    };
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let runDone = false;
  let streamFailure = null;
  const parser = createSseParser((frame) => {
    if (streamFailure) {
      return;
    }

    const frameType = String(frame?.event || '').trim();
    const payload = frame?.payload;
    let normalizedPayload = null;

    if (payload && typeof payload === 'object') {
      normalizedPayload = payload;
    } else if (typeof payload === 'string' && payload.trim()) {
      try {
        normalizedPayload = JSON.parse(payload);
      } catch {
        normalizedPayload = null;
      }
    }

    if (!normalizedPayload || typeof normalizedPayload !== 'object') {
      return;
    }

    const nextType = frameType && frameType !== 'message' ? frameType : String(normalizedPayload?.type || '').trim();
    const result = dispatchStreamPayload({
      dispatch,
      threadId: normalizedThreadId,
      runId: normalizedRunId,
      assistantEventId,
      payload: normalizedPayload,
      streamEventName: nextType
    });

    if (result?.streamFailure) {
      streamFailure = result.streamFailure;
      return;
    }

    if (result?.runDone) {
      runDone = true;
    }
  });

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        parser.flush();
        break;
      }

      if (!value) {
        continue;
      }

      parser.feed(decoder.decode(value, { stream: true }));
      if (streamFailure) {
        await reader.cancel();
        break;
      }
    }
  } catch (err) {
    if (streamFailure) {
      return streamFailure;
    }

    if (signal?.aborted) {
      return {
        success: false,
        canceled: true,
        retryable: false,
        message: 'Stream canceled'
      };
    }

    return {
      success: false,
      retryable: true,
      message: normalizeError(err)
    };
  }

  if (streamFailure) {
    return streamFailure;
  }

  if (!runDone) {
    return {
      success: false,
      retryable: true,
      message: 'Run stream ended before authoritative completion'
    };
  }

  return {
    success: true,
    done: true
  };
};

const runDirectRunStreamLoop = async ({ dispatch, getState, threadId, runId, assistantEventId, authToken, resolveAuthTokenForReconnect = null, signal, includeLastAppliedSeqOnInitialConnect = true } = {}) => {
  const maxReconnects = 20;
  const resolveStreamAuthToken = async () => {
    if (typeof resolveAuthTokenForReconnect === 'function') {
      try {
        const nextToken = await resolveAuthTokenForReconnect();
        const normalizedNextToken = String(nextToken || '').trim();

        if (normalizedNextToken) {
          return normalizedNextToken;
        }
      } catch {
        // fall through to static token
      }
    }

    const normalizedAuthToken = String(authToken || '').trim();
    return normalizedAuthToken || null;
  };

  for (let attempt = 0; attempt <= maxReconnects; attempt += 1) {
    if (signal?.aborted) {
      return {
        success: false,
        canceled: true
      };
    }

    const streamAuthToken = await resolveStreamAuthToken();

    if (attempt > 0) {
      await backfillThreadEventsFromSeq({
        dispatch,
        getState,
        threadId,
        authToken: streamAuthToken,
        limit: DEFAULT_STREAM_BACKFILL_LIMIT
      });
    }

    const streamRes = await openDirectRunStreamOnce({
      dispatch,
      getState,
      threadId,
      runId,
      assistantEventId,
      authToken: streamAuthToken,
      signal,
      includeLastAppliedSeq: attempt > 0 || includeLastAppliedSeqOnInitialConnect
    });

    if (streamRes?.success && streamRes.done) {
      await backfillThreadEventsFromSeq({
        dispatch,
        getState,
        threadId,
        authToken: streamAuthToken,
        limit: DEFAULT_STREAM_BACKFILL_LIMIT
      });

      return {
        success: true
      };
    }

    if (signal?.aborted || streamRes?.canceled) {
      return {
        success: false,
        canceled: true
      };
    }

    const isAuthFailureStatus = Number(streamRes?.status) === 401 || Number(streamRes?.status) === 403;

    if (!streamRes?.retryable && !isAuthFailureStatus) {
      return {
        success: false,
        message: streamRes?.message || 'Run stream ended unexpectedly'
      };
    }

    if (attempt >= maxReconnects) {
      break;
    }

    try {
      await sleep(Math.min(1000 * 2 ** attempt, 8000), signal);
    } catch {
      return {
        success: false,
        canceled: true
      };
    }
  }

  return {
    success: false,
    message: 'Run stream disconnected after multiple reconnect attempts'
  };
};

const appendStreamFailureBubble = ({ dispatch, threadId, runId, message } = {}) => {
  dispatch(
    upsertThreadEvent({
      threadId,
      event: normalizePieveThreadEventForPresentation({
        id: randomId(),
        type: 'bubble',
        className: 'assistant',
        lines: toLines(`Error: ${String(message || 'Run stream disconnected')}`),
        runId,
        createdAt: createTimestamp()
      })
    })
  );
};

const attachManagedDirectRunStream = ({ dispatch, getState, threadId, runId, state, composerLane, authToken, authTokenInput = null, assistantEventId = null, remoteStreamOwner, backfillBeforeConnect = false } = {}) => {
  return startManagedRemoteRunStream({
    activeRunStateByRunId,
    threadId,
    runId,
    state,
    authToken,
    authTokenInput,
    assistantEventId,
    remoteStreamOwner,
    backfillBeforeConnect,
    shouldAttachRemoteRunStream: shouldAttachDirectRemoteRunStream,
    emitThreadRunState: (payload) => {
      dispatch(setThreadRunState({ ...payload, composerLane }));
    },
    backfillEventsFromSeq: ({ threadId: nextThreadId, authToken: nextAuthToken }) => {
      return backfillThreadEventsFromSeq({
        dispatch,
        getState,
        threadId: nextThreadId,
        authToken: nextAuthToken
      });
    },
    streamRemoteRunLoop: ({ threadId: nextThreadId, runId: nextRunId, assistantEventId: nextAssistantEventId, authToken: nextAuthToken, resolveAuthTokenForReconnect, signal }) => {
      return runDirectRunStreamLoop({
        dispatch,
        getState,
        threadId: nextThreadId,
        runId: nextRunId,
        assistantEventId: nextAssistantEventId,
        authToken: nextAuthToken,
        resolveAuthTokenForReconnect,
        signal,
        // Fresh explicit sends need the run backlog replay because existing-thread
        // start responses do not carry an assistant event anchor yet.
        includeLastAppliedSeqOnInitialConnect: backfillBeforeConnect === true
      });
    },
    resolveAuthTokenForReconnect: async () => {
      return getPieveAuthToken();
    },
    onStreamFailure: async ({ threadId: failedThreadId, runId: failedRunId, streamResult }) => {
      dispatch(setThreadRunState({ threadId: failedThreadId, runId: failedRunId, state: 'error' }));
      appendStreamFailureBubble({ dispatch, threadId: failedThreadId, runId: failedRunId, message: streamResult?.message || 'Run stream disconnected' });
    }
  });
};

const buildRequestClientPayload = ({ runtimeClient } = {}) => {
  const kind = String(runtimeClient?.kind || '').trim();
  const platform = String(runtimeClient?.platform || '').trim();

  if (!kind && !platform) {
    return undefined;
  }

  return {
    ...(kind ? { kind } : {}),
    ...(platform ? { platform } : {}),
    version: 1
  };
};

const isDesktopRuntimeClient = ({ runtimeClient } = {}) => {
  const kind = String(runtimeClient?.kind || '')
    .trim()
    .toLowerCase();

  return kind === 'desktop' || runtimeClient?.capabilities?.isDesktop === true;
};

const getRelayIdFromDesktopAuthToken = ({ authToken } = {}) => {
  const payload = decodeJwtPayload(authToken);
  const relayId = String(payload?.relayId || '').trim();

  if (payload?.aud !== 'pieve-desktop-relay' || !relayId) {
    return null;
  }

  return relayId;
};

const buildRunRuntimeContext = ({ runtimeClient, authToken, projectId, projectName, projectWorkingDir, projectFolderName, sandboxMode, devTrace, runtime, inputProvenance } = {}) => {
  const runtimeContext = getWebRuntimeContext({
    projectId,
    projectName,
    projectWorkingDir,
    projectFolderName,
    sandboxMode,
    devTrace,
    runtime,
    inputProvenance
  });

  if (!isDesktopRuntimeClient({ runtimeClient })) {
    return runtimeContext;
  }

  const relayId = getRelayIdFromDesktopAuthToken({ authToken });
  const platform = String(runtimeClient?.platform || '')
    .trim()
    .toLowerCase();
  const runtimeCapabilities = runtimeClient?.capabilities && typeof runtimeClient.capabilities === 'object' && !Array.isArray(runtimeClient.capabilities) ? runtimeClient.capabilities : {};

  return {
    ...runtimeContext,
    kind: 'desktop',
    platform: platform || 'desktop',
    workspaceType: 'local',
    capabilities: {
      ...runtimeContext.capabilities,
      ...runtimeCapabilities,
      isDesktop: true
    },
    ...(relayId ? { relayId } : {})
  };
};

const startDirectRemoteThreadRun = async ({ dispatch, getState, runtimeClient = null, thread, threadId, prompt, newUserInput = null, eventUpdate = null, model, reasoning, authToken, projectId = null, projectName = null, projectWorkingDir = null, projectFolderName = null, runtime = null, inputProvenance = null, devTrace = null, optimisticIdempotencyKey = null } = {}) => {
  const normalizedThreadId = getNormalizedThreadId(threadId);

  if (!normalizedThreadId) {
    return {
      success: false,
      data: {
        message: 'threadId is required'
      }
    };
  }

  const normalizedPrompt = String(prompt || '').trim();
  const normalizedNewUserInput = Array.isArray(newUserInput) ? newUserInput : [];
  const textFromNewUserInput = normalizedNewUserInput
    .filter((item) => {
      return (
        String(item?.type || '')
          .trim()
          .toLowerCase() === 'text' && String(item?.text || '').trim()
      );
    })
    .map((item) => {
      return String(item?.text || '').trim();
    })
    .join('\n\n')
    .trim();
  const effectivePrompt = normalizedPrompt || textFromNewUserInput;

  if (!effectivePrompt && normalizedNewUserInput.length < 1 && !eventUpdate) {
    return {
      success: false,
      data: {
        message: 'prompt or newUserInput is required'
      }
    };
  }

  const normalizedAuthToken = String(authToken || '').trim();
  const idempotencyKey = String(optimisticIdempotencyKey || '').trim() || randomId();
  const optimisticRunId = `pending-${idempotencyKey}`;
  const pendingUserEventId = `pending-user-${idempotencyKey}`;
  const pendingUserText = effectivePrompt;

  if (pendingUserText || normalizedNewUserInput.length > 0) {
    appendOptimisticUserBubble({
      dispatch,
      getState,
      threadId: normalizedThreadId,
      eventId: pendingUserEventId,
      runId: optimisticRunId,
      text: pendingUserText,
      createdAt: createTimestamp(),
      inputItems: normalizedNewUserInput
    });
  }
  dispatch(
    setThreadRunState({
      threadId: normalizedThreadId,
      runId: optimisticRunId,
      state: 'generating'
    })
  );

  const runtimeProjectContext = resolveRuntimeProjectContext({
    thread,
    projectId,
    projectName,
    projectWorkingDir,
    projectFolderName
  });
  const modelId = model;
  const reasoningPayload = getModelReasoningLevels(modelId).includes(reasoning) ? reasoning : undefined;
  const requestClient = buildRequestClientPayload({
    runtimeClient
  });
  const baseStartPayload = {
    threadId: normalizedThreadId,
    idempotencyKey,
    newUserMessage: effectivePrompt || undefined,
    ...(normalizedNewUserInput.length > 0 ? { newUserInput: normalizedNewUserInput } : {}),
    ...(eventUpdate ? { eventUpdate } : {}),
    model: modelId,
    reasoning: reasoningPayload,
    runtime: buildRunRuntimeContext({
      runtimeClient,
      authToken: normalizedAuthToken,
      projectId: runtimeProjectContext.projectId,
      projectName: runtimeProjectContext.projectName,
      projectWorkingDir: runtimeProjectContext.projectWorkingDir,
      projectFolderName: runtimeProjectContext.projectFolderName,
      sandboxMode: runtimeProjectContext.sandboxMode,
      devTrace,
      runtime,
      inputProvenance
    }),
    ...(requestClient
      ? {
          client: requestClient
        }
      : {})
  };

  const startPayload = baseStartPayload;
  const startRes = await startThreadRun({
    ...startPayload,
    authToken: normalizedAuthToken
  });

  if (!startRes?.success) {
    const status = startRes?.status ?? null;
    const startData = startRes?.data && typeof startRes.data === 'object' ? startRes.data : {};
    const activeRunId = startData?.runId || startData?.run?.runId || startData?.run?.id || null;
    const alreadyActive =
      Number(status) === 409 ||
      String(startRes?.message || '')
        .toLowerCase()
        .includes('already active');

    if (alreadyActive && activeRunId) {
      if (!normalizedPrompt) {
        dispatch(
          deleteThreadEvent({
            threadId: normalizedThreadId,
            eventId: pendingUserEventId
          })
        );
        dispatch(
          setThreadRunState({
            threadId: normalizedThreadId,
            runId: optimisticRunId,
            state: 'error'
          })
        );

        return {
          success: false,
          message: 'Attachments cannot be steered into an active run.',
          code: 'MODEL_CAPABILITY_MISMATCH',
          status
        };
      }

      const steerRes = await steerThreadRun({
        threadId: normalizedThreadId,
        runId: activeRunId,
        expectedRunId: activeRunId,
        input: normalizedPrompt,
        idempotencyKey,
        authToken: normalizedAuthToken
      });

      if (!steerRes?.success) {
        dispatch(
          deleteThreadEvent({
            threadId: normalizedThreadId,
            eventId: pendingUserEventId
          })
        );
        dispatch(
          setThreadRunState({
            threadId: normalizedThreadId,
            runId: optimisticRunId,
            state: 'error'
          })
        );

        return {
          success: false,
          message: steerRes?.message || 'Failed to steer active run',
          code: steerRes?.code || 'run_steer_failed',
          status: steerRes?.status || null
        };
      }

      if (steerRes.data?.queued === true && steerRes.data?.idempotentReplay !== true) {
        syncConfirmedThreadUpdatedAt({
          dispatch,
          threadId: normalizedThreadId,
          threadUpdatedAt: steerRes.data.threadUpdatedAt,
          source: 'start-run-steer'
        });
      }

      dispatch(
        deleteThreadEvent({
          threadId: normalizedThreadId,
          eventId: pendingUserEventId
        })
      );

      attachManagedDirectRunStream({
        dispatch,
        getState,
        threadId: normalizedThreadId,
        runId: activeRunId,
        state: normalizeThreadRunState(startData?.state || 'generating'),
        authToken: normalizedAuthToken,
        authTokenInput: authToken,
        remoteStreamOwner: EXPLICIT_REMOTE_RUN_STREAM_OWNER,
        backfillBeforeConnect: true
      });

      return {
        success: true,
        data: {
          runId: activeRunId,
          alreadyActive: true,
          steered: true,
          steerQueued: steerRes?.data?.queued === true,
          steerIdempotentReplay: steerRes?.data?.idempotentReplay === true
        }
      };
    }

    dispatch(
      deleteThreadEvent({
        threadId: normalizedThreadId,
        eventId: pendingUserEventId
      })
    );
    dispatch(
      setThreadRunState({
        threadId: normalizedThreadId,
        runId: optimisticRunId,
        state: 'error'
      })
    );

    return startRes;
  }

  if (startRes.data?.resumed === false) {
    dispatch(
      setThreadRunState({
        threadId: normalizedThreadId,
        runId: optimisticRunId,
        state: 'idle'
      })
    );

    return {
      success: true,
      data: {
        resumed: false
      }
    };
  }

  const parsedStart = parseRunStartResponse(startRes.data);
  const runId = parsedStart.runId || optimisticRunId;
  syncConfirmedThreadUpdatedAt({
    dispatch,
    threadId: normalizedThreadId,
    threadUpdatedAt: parsedStart.threadUpdatedAt,
    source: 'start-run'
  });
  const rawUserEvent =
    parsedStart.userEvent ||
    (pendingUserText || normalizedNewUserInput.length > 0
      ? buildOptimisticUserEvent({
          eventId: parsedStart.userEventId || `run-${runId}-user`,
          runId,
          text: pendingUserText,
          createdAt: createTimestamp(),
          inputItems: normalizedNewUserInput
        })
      : null);
  const normalizedUserEvent = rawUserEvent ? normalizePieveThreadEventForPresentation(rawUserEvent) : null;

  if (normalizedUserEvent) {
    dispatch(
      upsertThreadEvent({
        threadId: normalizedThreadId,
        event: normalizedUserEvent
      })
    );
  }

  if (parsedStart.assistantEvent) {
    dispatch(
      handleThreadRuntimeEvent({
        type: 'event.appended',
        threadId: normalizedThreadId,
        runId,
        event: parsedStart.assistantEvent
      })
    );
  }

  attachManagedDirectRunStream({
    dispatch,
    getState,
    threadId: normalizedThreadId,
    runId,
    state: normalizeThreadRunState(parsedStart.runState || 'generating'),
    authToken: normalizedAuthToken,
    authTokenInput: authToken,
    assistantEventId: parsedStart.assistantEventId || parsedStart.assistantEvent?.id || null,
    remoteStreamOwner: EXPLICIT_REMOTE_RUN_STREAM_OWNER,
    backfillBeforeConnect: false
  });

  return {
    success: true,
    data: {
      runId,
      threadUpdatedAt: parsedStart.threadUpdatedAt,
      userEventId: parsedStart.userEventId || normalizedUserEvent.id,
      assistantEventId: parsedStart.assistantEventId || parsedStart.assistantEvent?.id || null,
      requestedModelId: parsedStart.requestedModelId || null,
      effectiveModelId: parsedStart.effectiveModelId || null,
      billing: parsedStart.billing || null
    }
  };
};

const steerDirectRemoteThreadRun = async ({ dispatch, getState, threadId, runId, expectedRunId, input, authToken } = {}) => {
  const steerRes = await steerThreadRun({
    threadId,
    runId,
    expectedRunId,
    input,
    authToken
  });
  const normalizedThreadId = getNormalizedThreadId(threadId);
  const normalizedRunId = getNormalizedRunId(runId);

  if (!steerRes?.success) {
    return steerRes;
  }

  if (steerRes.data?.queued === true && steerRes.data?.idempotentReplay !== true) {
    syncConfirmedThreadUpdatedAt({
      dispatch,
      threadId: normalizedThreadId,
      threadUpdatedAt: steerRes.data.threadUpdatedAt,
      source: 'steer-run'
    });
  }

  if (!activeRunStateByRunId.has(normalizedRunId)) {
    attachManagedDirectRunStream({
      dispatch,
      getState,
      threadId: normalizedThreadId,
      runId: normalizedRunId,
      state: normalizeThreadRunState(steerRes?.data?.state || 'generating'),
      authToken,
      remoteStreamOwner: EXPLICIT_REMOTE_RUN_STREAM_OWNER,
      backfillBeforeConnect: true
    });
  }

  return {
    success: true,
    data: {
      runId: normalizedRunId,
      threadUpdatedAt: steerRes?.data?.threadUpdatedAt || null,
      queued: steerRes?.data?.queued === true,
      idempotentReplay: steerRes?.data?.idempotentReplay === true,
      queueSize: Number(steerRes?.data?.queueSize || 0),
      enqueuedAt: steerRes?.data?.enqueuedAt || null
    }
  };
};

const cancelDirectRemoteThreadRun = async ({ dispatch, getState, threadId, runId, authToken } = {}) => {
  const normalizedThreadId = getNormalizedThreadId(threadId);
  const normalizedRunId = getNormalizedRunId(runId);
  const cancelRes = await cancelThreadRun({
    threadId: normalizedThreadId,
    runId: normalizedRunId,
    authToken
  });

  if (!cancelRes?.success) {
    return cancelRes;
  }

  const activeRunState = activeRunStateByRunId.get(normalizedRunId);
  const canceledRun = cancelRes?.data?.run && typeof cancelRes.data.run === 'object' ? cancelRes.data.run : {};
  const nextRunId = getNormalizedRunId(canceledRun?.runId || canceledRun?.id || normalizedRunId) || normalizedRunId;
  const nextState = normalizeThreadRunState(canceledRun?.state || cancelRes?.data?.state || 'canceling') || 'canceling';

  if (normalizedThreadId) {
    dispatch(
      setThreadRunState({
        threadId: normalizedThreadId,
        runId: nextRunId,
        state: nextState
      })
    );
  }

  if (normalizedThreadId) {
    await Promise.all([
      dispatch(
        hydrateThreadMeta({
          threadId: normalizedThreadId
        })
      ),
      backfillThreadEventsFromSeq({
        dispatch,
        getState,
        threadId: normalizedThreadId,
        authToken
      })
    ]);
  }

  const canonicalRunState = getCanonicalThreadRunStateFromState({
    getState,
    threadId: normalizedThreadId
  });
  const canonicalRunId = getNormalizedRunId(canonicalRunState?.runId) || nextRunId;
  const canonicalState = normalizeThreadRunState(canonicalRunState?.state || nextState) || nextState;

  if (shouldAttachDirectRemoteRunStream(canonicalState)) {
    if (!activeRunStateByRunId.has(canonicalRunId)) {
      attachManagedDirectRunStream({
        dispatch,
        getState,
        threadId: normalizedThreadId,
        runId: canonicalRunId,
        state: canonicalState,
        authToken,
        remoteStreamOwner: activeRunState?.remoteStreamOwner || EXPLICIT_REMOTE_RUN_STREAM_OWNER,
        backfillBeforeConnect: false
      });
    }

    return {
      success: true,
      data: {
        ok: true,
        state: canonicalState,
        runId: canonicalRunId
      }
    };
  }

  if (activeRunState?.controller && typeof activeRunState.controller.abort === 'function') {
    activeRunState.controller.abort();
  }

  activeRunStateByRunId.delete(normalizedRunId);

  return {
    success: true,
    data: {
      ok: true,
      state: canonicalState,
      runId: canonicalRunId
    }
  };
};

const attachHydratedDirectRemoteRunStreams = async ({ dispatch, getState, threads, authToken } = {}) => {
  const normalizedAuthToken = String(authToken || '').trim();
  const safeThreads = Array.isArray(threads) ? threads : [];
  // TEMP CAP: limit hydration stream connections to avoid exhausting the
  // browser's per-origin connection pool (typically 6). The collab stream
  // already covers state updates for threads beyond this cap.
  const MAX_HYDRATION_STREAMS = 3;
  let attachedCount = 0;

  for (const thread of safeThreads) {
    if (attachedCount >= MAX_HYDRATION_STREAMS) {
      break;
    }

    const normalizedThreadId = getNormalizedThreadId(thread?.id || thread?.threadId);

    if (!normalizedThreadId) {
      continue;
    }

    const runState = normalizeThreadRunStatePayload({
      run: thread?.runState || {},
      fallbackRunId: thread?.runState?.runId || null,
      fallbackState: thread?.runState?.state || 'idle'
    });
    const normalizedRunId = getNormalizedRunId(runState?.runId);

    if (!normalizedRunId || !shouldAttachDirectRemoteRunStream(runState?.state)) {
      continue;
    }

    // A send that is still waiting on its start request only has a local placeholder id.
    // The server has no run by that id, and the send attaches the stream once the real id lands.
    if (normalizedRunId.startsWith('pending-')) {
      continue;
    }

    attachRemoteThreadRunStream({ dispatch, getState, threadId: normalizedThreadId, runId: normalizedRunId, state: runState.state, authToken: normalizedAuthToken });
    attachedCount += 1;
  }
};

// Follows a run this device did not start (found in a thread list, or started on another device): it backfills the
// events this device missed, then streams the rest.
const attachRemoteThreadRunStream = ({ dispatch, getState, threadId, runId, state, composerLane, authToken }) => {
  attachManagedDirectRunStream({
    dispatch,
    getState,
    threadId,
    runId,
    state,
    composerLane,
    authToken,
    remoteStreamOwner: HYDRATED_REMOTE_RUN_STREAM_OWNER,
    backfillBeforeConnect: true
  });
};

const isDirectRemoteRunManaged = ({ threadId, runId } = {}) => {
  const normalizedRunId = getNormalizedRunId(runId);

  if (normalizedRunId && activeRunStateByRunId.has(normalizedRunId)) {
    return true;
  }

  const normalizedThreadId = getNormalizedThreadId(threadId);

  if (!normalizedThreadId) {
    return false;
  }

  for (const activeRunState of activeRunStateByRunId.values()) {
    if (getNormalizedThreadId(activeRunState?.threadId) === normalizedThreadId) {
      return true;
    }
  }

  return false;
};

// The run-stream pieces a run started elsewhere (a plan run on Pieve Desktop) follows from `attachPlanRunStream.js`.
export { EXPLICIT_REMOTE_RUN_STREAM_OWNER, attachManagedDirectRunStream, backfillThreadEventsFromSeq, getNormalizedRunId, getNormalizedThreadId };
export { attachHydratedDirectRemoteRunStreams, attachRemoteThreadRunStream, cancelDirectRemoteThreadRun, isDirectRemoteRunManaged, isDirectRemoteRunManagedEventType, startDirectRemoteThreadRun, steerDirectRemoteThreadRun };
